import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import PageLayout from '../../components/ui/PageLayout';
import { diagnosticsService } from '../../services/diagnosticsService';

import Button from '../../components/ui/Button';
import OverallHealthIndicator from './components/OverallHealthIndicator';
import ServiceStatusCard from './components/ServiceStatusCard';
import IntegrationStatusPanel from './components/IntegrationStatusPanel';
import PerformanceMetricsChart from './components/PerformanceMetricsChart';

const SystemDiagnostics = () => {
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);

  const runDiagnostics = async () => {
    try {
      setLoading(true);
      const results = await diagnosticsService?.runFullDiagnostics();
      setDiagnostics(results);
      setLastChecked(new Date());
    } catch (err) {
      console.error('Error running diagnostics:', err);
      setDiagnostics({ overall: 'error', services: {} });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runDiagnostics();
  }, []);

  const services = diagnostics?.services ? Object.entries(diagnostics?.services)?.map(([name, info]) => ({
    name: name?.charAt(0)?.toUpperCase() + name?.slice(1),
    ...info
  })) : [];

  return (
    <PageLayout>
      <Helmet>
        <title>System Diagnostics - LeadGen Pro</title>
        <meta name="description" content="Monitor system health and service status" />
      </Helmet>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">System Diagnostics</h1>
          <p className="text-muted-foreground">
            {lastChecked ? `Last checked: ${lastChecked?.toLocaleTimeString()}` : 'Running diagnostics...'}
          </p>
        </div>
        <Button variant="outline" iconName="RefreshCw" iconPosition="left" onClick={runDiagnostics} disabled={loading}>
          {loading ? 'Running...' : 'Run Diagnostics'}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Running system diagnostics...</p>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <OverallHealthIndicator status={diagnostics?.overall} />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {services?.map((service, index) => (
              <ServiceStatusCard key={index} service={service} />
            ))}
          </div>

          <IntegrationStatusPanel services={diagnostics?.services} />
          <PerformanceMetricsChart />
        </div>
      )}
    </PageLayout>
  );
};

export default SystemDiagnostics;